import { Injectable } from '@angular/core';
import {
  collection,
  deleteDoc,
  doc,
  docData,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  increment,
  Timestamp,
  updateDoc,
  writeBatch,
} from '@angular/fire/firestore';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { encodeStrict, toRawStockCode, toStockDocId } from '../../../shared/shared-utils/firestore/firestore.utils';
import {
  FIRESTORE_TARGET_DOC_BYTES,
  mergeEntriesIntoChunks,
} from '../utils/stock-take-chunked-storage.utils';
import {
  IStockTakeItem,
  IStockTakeItemChunkDoc,
  IStockTakeItemStored,
  IStockTakeItemsMeta,
  StockTakeItemStatus,
} from '../models';
import { IStockTakeItemCapacity } from '../utils/stock-take-limits.utils';

interface IResolvedStockTakeItemEntry {
  chunkId: string;
  key: string;
}

/** Singleton meta document under the items_meta subcollection (path must have even segment count). */
const ITEMS_META_DOC_ID = '_meta';

const itemsMetaPath = (stockTakePath: string): string =>
  `${stockTakePath}/items_meta/${ITEMS_META_DOC_ID}`;

const itemChunkPath = (stockTakePath: string, chunkId: string): string =>
  `${stockTakePath}/item_chunks/${chunkId}`;

const itemChunksCollectionPath = (stockTakePath: string): string => `${stockTakePath}/item_chunks`;

/**
 * Chunked Firestore storage for stock take line items.
 */
@Injectable({
  providedIn: 'root',
})
export class StockTakeItemsChunkService {
  constructor(private readonly firestore: Firestore) {}

  /**
   * Real-time stream of all items for a stock take (chunked layout only).
   */
  watchStockTakeItems(stockTakePath: string): Observable<IStockTakeItem[]> {
    const metaRef = doc(this.firestore, itemsMetaPath(stockTakePath));
    return docData(metaRef).pipe(
      switchMap((metaRaw: DocumentData | undefined) => {
        const meta = metaRaw as IStockTakeItemsMeta | undefined;
        if (!meta?.chunkDocIds?.length) {
          return of([]);
        }
        const chunkStreams = meta.chunkDocIds.map((chunkId: string) =>
          docData(doc(this.firestore, itemChunkPath(stockTakePath, chunkId))).pipe(
            map((chunkRaw: DocumentData | undefined) => {
              const chunk = chunkRaw as IStockTakeItemChunkDoc | undefined;
              return chunk?.entries ?? {};
            }),
          ),
        );
        return combineLatest(chunkStreams).pipe(
          map((entryMaps: Record<string, IStockTakeItemStored>[]) =>
            this.flattenEntries(entryMaps),
          ),
        );
      }),
    );
  }

  /**
   * Load items once from chunked storage.
   */
  async loadStockTakeItems(stockTakePath: string): Promise<IStockTakeItem[]> {
    const meta = await this.getMeta(stockTakePath);
    if (!meta?.chunkDocIds?.length) {
      return [];
    }
    const entryMaps: Record<string, IStockTakeItemStored>[] = [];
    for (const chunkId of meta.chunkDocIds) {
      const snap = await getDoc(doc(this.firestore, itemChunkPath(stockTakePath, chunkId)));
      if (snap.exists()) {
        const chunk = snap.data() as IStockTakeItemChunkDoc;
        entryMaps.push(chunk.entries ?? {});
      }
    }
    return this.flattenEntries(entryMaps);
  }

  /**
   * Append new items into chunked storage.
   */
  async batchWriteItems(
    stockTakePath: string,
    items: Omit<IStockTakeItem, 'id'>[],
  ): Promise<void> {
    if (items.length === 0) {
      return;
    }
    const now = Timestamp.now();
    const entryList = items.map((item) => {
      const rawCode = toRawStockCode(item.code);
      const key = toStockDocId(item.code);
      const stored: IStockTakeItemStored = { addedAt: now, ...item, code: rawCode };
      return { key, value: stored };
    });
    let meta = await this.getMeta(stockTakePath);
    if (!meta) {
      meta = {
        schemaVersion: 2,
        itemCount: 0,
        chunkDocIds: [],
        codeToChunkId: {},
      };
    }
    const existingChunks: { chunkId: string; entries: Record<string, IStockTakeItemStored> }[] = [];
    for (const chunkId of meta.chunkDocIds) {
      const snap = await getDoc(doc(this.firestore, itemChunkPath(stockTakePath, chunkId)));
      if (snap.exists()) {
        const chunk = snap.data() as IStockTakeItemChunkDoc;
        existingChunks.push({ chunkId, entries: chunk.entries ?? {} });
      }
    }
    const mergedPlans = mergeEntriesIntoChunks(existingChunks, entryList, FIRESTORE_TARGET_DOC_BYTES, 'chunk');
    const batch = writeBatch(this.firestore);
    const chunkDocIds = mergedPlans.map((p) => p.chunkId);
    const codeToChunkId: Record<string, string> = {};
    for (const plan of mergedPlans) {
      const ref = doc(this.firestore, itemChunkPath(stockTakePath, plan.chunkId));
      batch.set(ref, { schemaVersion: 2, entries: plan.entries });
      for (const key of Object.keys(plan.entries)) {
        codeToChunkId[key] = plan.chunkId;
      }
    }
    batch.set(doc(this.firestore, itemsMetaPath(stockTakePath)), {
      schemaVersion: 2,
      itemCount: meta.itemCount + items.length,
      chunkDocIds,
      codeToChunkId,
    });
    await batch.commit();
  }

  /**
   * Update entries inside chunk documents by stock code.
   * Returns how many item lines were actually patched.
   */
  async batchUpdateItems(
    stockTakePath: string,
    updates: {
      code: string;
      patch: Partial<IStockTakeItemStored>;
    }[],
  ): Promise<number> {
    const meta = await this.getMeta(stockTakePath);
    if (!meta || updates.length === 0) {
      return 0;
    }
    const chunkCache = new Map<string, Record<string, IStockTakeItemStored>>();
    const changedChunkIds = new Set<string>();
    const loadChunk = async (chunkId: string): Promise<Record<string, IStockTakeItemStored> | null> => {
      const cached = chunkCache.get(chunkId);
      if (cached) {
        return cached;
      }
      const snap = await getDoc(doc(this.firestore, itemChunkPath(stockTakePath, chunkId)));
      if (!snap.exists()) {
        return null;
      }
      const chunk = snap.data() as IStockTakeItemChunkDoc;
      const entries = { ...(chunk.entries ?? {}) };
      chunkCache.set(chunkId, entries);
      return entries;
    };
    const applyPatch = (
      entries: Record<string, IStockTakeItemStored>,
      chunkId: string,
      key: string,
      patch: Partial<IStockTakeItemStored>,
    ): boolean => {
      const existing = entries[key];
      if (!existing) {
        return false;
      }
      entries[key] = { ...existing, ...patch };
      changedChunkIds.add(chunkId);
      return true;
    };

    let applied = 0;
    const unmapped: { code: string; patch: Partial<IStockTakeItemStored> }[] = [];
    for (const update of updates) {
      const keys = this.getEntryKeysForCode(update.code);
      let found = false;
      for (const key of keys) {
        const chunkId = meta.codeToChunkId[key];
        if (!chunkId) {
          continue;
        }
        const entries = await loadChunk(chunkId);
        if (entries && applyPatch(entries, chunkId, key, update.patch)) {
          applied += 1;
          found = true;
        }
      }
      if (!found) {
        unmapped.push(update);
      }
    }

    if (unmapped.length > 0) {
      for (const chunkId of meta.chunkDocIds) {
        const entries = await loadChunk(chunkId);
        if (!entries) {
          continue;
        }
        for (const update of unmapped) {
          for (const key of this.getEntryKeysForCode(update.code)) {
            if (applyPatch(entries, chunkId, key, update.patch)) {
              applied += 1;
            }
          }
        }
      }
    }

    if (applied === 0) {
      return 0;
    }

    const chunkIds = Array.from(changedChunkIds);
    const batchLimit = 450;
    for (let i = 0; i < chunkIds.length; i += batchLimit) {
      const batch = writeBatch(this.firestore);
      for (const chunkId of chunkIds.slice(i, i + batchLimit)) {
        const entries = chunkCache.get(chunkId);
        if (!entries) {
          continue;
        }
        batch.set(doc(this.firestore, itemChunkPath(stockTakePath, chunkId)), {
          schemaVersion: 2,
          entries,
        });
      }
      await batch.commit();
    }
    return applied;
  }

  /**
   * Update a single item line by code.
   */
  async updateItemByCode(
    stockTakePath: string,
    code: string,
    patch: Partial<IStockTakeItemStored>,
  ): Promise<void> {
    const applied = await this.batchUpdateItems(stockTakePath, [{ code, patch }]);
    if (applied === 0) {
      throw new Error(`Stock take item "${code}" could not be updated`);
    }
  }

  /**
   * Remove items by stock codes.
   */
  async removeItemsByCodes(
    stockTakePath: string,
    codesToRemove: string[],
  ): Promise<IStockTakeItem[]> {
    const meta = await this.getMeta(stockTakePath);
    if (!meta || codesToRemove.length === 0) {
      return [];
    }
    const batch = writeBatch(this.firestore);
    const removedItems: IStockTakeItem[] = [];
    const codeToChunkId = { ...meta.codeToChunkId };
    const byChunk = new Map<string, Set<string>>();
    for (const code of codesToRemove) {
      const resolvedEntries = await this.resolveEntriesForCode(stockTakePath, meta, code);
      if (resolvedEntries.length === 0) {
        continue;
      }
      for (const resolvedEntry of resolvedEntries) {
        const set = byChunk.get(resolvedEntry.chunkId) ?? new Set<string>();
        set.add(resolvedEntry.key);
        byChunk.set(resolvedEntry.chunkId, set);
        delete codeToChunkId[resolvedEntry.key];
      }
    }
    if (byChunk.size === 0) {
      return [];
    }
    for (const [chunkId, keys] of byChunk.entries()) {
      const ref = doc(this.firestore, itemChunkPath(stockTakePath, chunkId));
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        continue;
      }
      const chunk = snap.data() as IStockTakeItemChunkDoc;
      const entries = { ...chunk.entries };
      for (const key of keys) {
        const removedEntry = entries[key];
        if (removedEntry) {
          const code = toRawStockCode(key);
          removedItems.push({
            ...removedEntry,
            id: code,
            code,
          });
          delete entries[key];
        }
      }
      batch.set(ref, { schemaVersion: 2, entries });
    }
    const logicalRemovedItems = this.deduplicateItemsByCode(removedItems);
    if (logicalRemovedItems.length === 0) {
      return [];
    }
    batch.set(doc(this.firestore, itemsMetaPath(stockTakePath)), {
      ...meta,
      itemCount: Math.max(0, meta.itemCount - logicalRemovedItems.length),
      codeToChunkId,
    });
    await batch.commit();
    return logicalRemovedItems;
  }

  /**
   * Capacity counts from chunked items in memory.
   */
  async getItemCapacity(stockTakePath: string): Promise<IStockTakeItemCapacity> {
    const items = await this.loadStockTakeItems(stockTakePath);
    const unique = items.filter((i) => i.status === StockTakeItemStatus.unique).length;
    return {
      total: items.length,
      unique,
      base: items.length - unique,
    };
  }

  /**
   * Delete all chunk documents and meta for a stock take.
   */
  async deleteAllChunks(stockTakePath: string): Promise<void> {
    const meta = await this.getMeta(stockTakePath);
    const chunkCol = collection(this.firestore, itemChunksCollectionPath(stockTakePath));
    const legacySnap = await getDocs(chunkCol);
    const refs = legacySnap.docs.map((d) => d.ref);
    if (meta?.chunkDocIds) {
      for (const chunkId of meta.chunkDocIds) {
        refs.push(doc(this.firestore, itemChunkPath(stockTakePath, chunkId)));
      }
    }
    const batchLimit = 450;
    for (let i = 0; i < refs.length; i += batchLimit) {
      const batch = writeBatch(this.firestore);
      for (const ref of refs.slice(i, i + batchLimit)) {
        batch.delete(ref);
      }
      await batch.commit();
    }
    await deleteDoc(doc(this.firestore, itemsMetaPath(stockTakePath))).catch(() => undefined);
  }

  /**
   * Whether chunked items exist for this stock take.
   */
  async hasChunkedItems(stockTakePath: string): Promise<boolean> {
    const meta = await this.getMeta(stockTakePath);
    return !!meta && meta.itemCount > 0;
  }

  private async getMeta(stockTakePath: string): Promise<IStockTakeItemsMeta | null> {
    const snap = await getDoc(doc(this.firestore, itemsMetaPath(stockTakePath)));
    if (!snap.exists()) {
      return null;
    }
    return snap.data() as IStockTakeItemsMeta;
  }

  private flattenEntries(entryMaps: Record<string, IStockTakeItemStored>[]): IStockTakeItem[] {
    const items: IStockTakeItem[] = [];
    for (const map of entryMaps) {
      for (const [encoded, stored] of Object.entries(map)) {
        const code = toRawStockCode(encoded);
        items.push({
          ...stored,
          id: code,
          code,
        });
      }
    }
    return this.deduplicateItemsByCode(items);
  }

  /**
   * Resolve the chunk entries that store a stock code, falling back to a
   * chunk scan when items_meta.codeToChunkId is stale or incomplete.
   */
  private async resolveEntriesForCode(
    stockTakePath: string,
    meta: IStockTakeItemsMeta,
    code: string,
  ): Promise<IResolvedStockTakeItemEntry[]> {
    const keys = this.getEntryKeysForCode(code);
    const resolvedEntries = new Map<string, IResolvedStockTakeItemEntry>();
    for (const key of keys) {
      const mapped = meta.codeToChunkId[key];
      if (mapped) {
        this.setResolvedEntry(resolvedEntries, mapped, key);
      }
    }
    for (const chunkId of meta.chunkDocIds) {
      const snap = await getDoc(doc(this.firestore, itemChunkPath(stockTakePath, chunkId)));
      if (!snap.exists()) {
        continue;
      }
      const chunk = snap.data() as IStockTakeItemChunkDoc;
      for (const key of keys) {
        if (chunk.entries?.[key]) {
          this.setResolvedEntry(resolvedEntries, chunkId, key);
        }
      }
    }
    return Array.from(resolvedEntries.values());
  }

  private getEntryKeysForCode(code: string): string[] {
    const normalizedKey = toStockDocId(code);
    const legacyDoubleEncodedKey = encodeStrict(normalizedKey);
    return normalizedKey === legacyDoubleEncodedKey
      ? [normalizedKey]
      : [normalizedKey, legacyDoubleEncodedKey];
  }

  private setResolvedEntry(
    resolvedEntries: Map<string, IResolvedStockTakeItemEntry>,
    chunkId: string,
    key: string,
  ): void {
    resolvedEntries.set(`${chunkId}::${key}`, { chunkId, key });
  }

  private selectDuplicateDisplayItem(
    existing: IStockTakeItem,
    next: IStockTakeItem,
  ): IStockTakeItem {
    if (existing.status === StockTakeItemStatus.pending && next.status !== StockTakeItemStatus.pending) {
      return next;
    }
    if ((next.timesCounted ?? 0) > (existing.timesCounted ?? 0)) {
      return next;
    }
    return existing;
  }

  private deduplicateItemsByCode(items: IStockTakeItem[]): IStockTakeItem[] {
    const itemsByCode = new Map<string, IStockTakeItem>();
    for (const item of items) {
      const existing = itemsByCode.get(item.code);
      itemsByCode.set(item.code, existing ? this.selectDuplicateDisplayItem(existing, item) : item);
    }
    return Array.from(itemsByCode.values());
  }
}
